/**
 * World-model zustand store — a thin subscription wrapper around the pure
 * `WorldModel`. The renderer reads the current model each frame via
 * `useWorldModel.getState().model` (no per-frame re-render overhead); React
 * components that care about specific slices subscribe via `useWorldModel`
 * with a selector.
 *
 * Event application flows here from the WireClient message dispatcher —
 * `dispatchServerMessage(msg)` below threads the envelope through the
 * appropriate applier and notifies subscribers. D6+ UI surfaces (chat pane,
 * inspection panel, player list) can subscribe with narrow selectors for
 * cheap re-renders.
 */

import { create } from 'zustand';

import { phase0, debugLog } from '../log';
import { useToasts } from '../toasts';
import type {
  EventEnvelope,
  ServerMessage,
  WorldEvent,
} from '../wire';
import {
  applyEnvelope,
  applyEventBatch,
  applySnapshot,
} from './appliers';
import { createEmptyWorldModel, type WorldModel } from './model';

export interface WorldModelState {
  /** Active model — replaced on snapshot apply, mutated in place by
   *  event appliers (then reassigned to force subscribers to re-evaluate). */
  model: WorldModel;
  /** Counter bumped every mutation so renderers that read outside React
   *  can detect changes without deep comparison. */
  version: number;
  /** Identity of the local player — populated on `HandshakeAck`. Used by
   *  the cutaway check (§4.5), the minimap locator (§15), and the
   *  moderation UI (§14.5) to answer "is this me?". */
  selfPlayerId: bigint | null;
  /** Flips `true` once the first snapshot has applied. The renderer uses
   *  this to gate one-shot UX effects (camera centering on self) so they
   *  don't fire on the placeholder fields pre-seeded by `HandshakeAck`
   *  (GH#7 C2 pre-seed sets position to `[0, 0]`; the real spawn coords
   *  arrive in the snapshot). */
  snapshotApplied: boolean;
  /** Tick hertz from the server — drives movement interpolation duration
   *  (the renderer lerps positions across `1000 / tick_hz` ms). */
  tickHz: number;

  // ─── Actions ───────────────────────────────────────────────────────────
  applyServerMessage: (msg: ServerMessage) => void;
  applyEvents: (tick: bigint, events: WorldEvent[]) => void;
  applyEnvelopeAction: (env: EventEnvelope) => void;
  reset: () => void;
}

export const useWorldModel = create<WorldModelState>((set) => ({
  model: createEmptyWorldModel(),
  version: 0,
  selfPlayerId: null,
  snapshotApplied: false,
  tickHz: 2,

  applyServerMessage: (msg) =>
    set((s) => {
      if (msg.type === 'HandshakeAck') {
        const ack = msg.payload;
        // GH#7 C2: pre-seed the self-player using the authoritative
        // `display_name` from the handshake. Without this, a Replay-mode
        // reconnect (or any path where `PlayerMoved` fires before a
        // `PlayerJoined` / snapshot rehydration) would create the player
        // record via `ensurePlayer`'s `player-${id}` fallback and the
        // avatar label would read "player-1" instead of "Alice".
        // Subsequent snapshot hydration / `PlayerJoined` events overwrite
        // the placeholder fields (position / facing) but the correct
        // display_name carries through.
        if (!s.model.players.has(ack.player_id)) {
          s.model.players.set(ack.player_id, {
            id: ack.player_id,
            display_name: ack.display_name,
            position: [0, 0],
            facing: 'South',
            role: 'Player',
            connected: true,
            recent_utterances: [],
            navGoal: null,
            z_level: 0,
          });
        } else {
          const existing = s.model.players.get(ack.player_id)!;
          existing.display_name = ack.display_name;
        }
        return {
          selfPlayerId: ack.player_id,
          tickHz: ack.tick_hz,
          model: s.model,
          version: s.version + 1,
        };
      }
      if (msg.type === 'Snapshot') {
        const t0 = performance.now();
        const [snapW, snapH] = msg.payload.world.bounds;
        phase0.info('snapshot_apply_begin', {
          tick: msg.payload.tick,
          width: snapW,
          height: snapH,
          players: msg.payload.players.length,
        });
        debugLog('world', 'info', `snapshot_apply_begin tick=${msg.payload.tick} ${snapW}x${snapH} players=${msg.payload.players.length}`);
        const model = applySnapshot(s.model, msg.payload);
        phase0.info(
          'snapshot_apply_end',
          { tick: msg.payload.tick },
          performance.now() - t0,
        );
        debugLog('world', 'info', `snapshot_apply_end tick=${msg.payload.tick} duration=${(performance.now() - t0).toFixed(1)}ms`);
        return { model, version: s.version + 1, snapshotApplied: true };
      }
      if (msg.type === 'Event') {
        const t0 = performance.now();
        applyEnvelope(s.model, msg.payload);
        if (msg.payload.envelope_kind === 'WorldEvents') {
          for (const event of msg.payload.data.events) {
            if ('FishCaught' in event) {
              const { entity, species } = event.FishCaught;
              if ('Player' in entity && entity.Player === s.selfPlayerId) {
                useToasts.getState().push('success', `Caught a ${species}!`);
              }
            } else if ('FishGotAway' in event) {
              const { entity, line_break } = event.FishGotAway;
              if ('Player' in entity && entity.Player === s.selfPlayerId) {
                useToasts.getState().push(
                  'info',
                  line_break ? 'Line break! The fish escaped.' : 'The fish got away.',
                );
              }
            }
          }
          phase0.debug(
            'event_batch_applied',
            {
              tick: msg.payload.data.tick,
              count: msg.payload.data.events.length,
            },
            performance.now() - t0,
          );
          debugLog('world', 'debug', `event_batch tick=${msg.payload.data.tick} count=${msg.payload.data.events.length} duration=${(performance.now() - t0).toFixed(1)}ms`);
        }
        return { model: s.model, version: s.version + 1 };
      }
      // Ping / Error don't touch world state; the wire layer handles them.
      return s;
    }),

  applyEvents: (tick, events) =>
    set((s) => {
      applyEventBatch(s.model, tick, events);
      return { model: s.model, version: s.version + 1 };
    }),

  applyEnvelopeAction: (env) =>
    set((s) => {
      applyEnvelope(s.model, env);
      return { model: s.model, version: s.version + 1 };
    }),

  reset: () =>
    set(() => ({
      model: createEmptyWorldModel(),
      version: 0,
      selfPlayerId: null,
      snapshotApplied: false,
      tickHz: 2,
    })),
}));
